import { API } from '@/api/API'
import { EventAndFirstSession, EventAndSessions, nestEventsApiGetEventsResponse } from '@/api/Helpers'
import type { Session } from '@/api/Sessions'
import { normalizeSession, sortEventSessionsByDate } from '@/helpers/SessionHelpers'
import Venue from '../helpers/Venue'

/**
 * @deprecated Use fetchAvailableEvents().
 */
export interface GetEventsResponse {
  eventTemplates: EventTemplate[]
  eventSessions: EventSession[]
  ticketGroups: TicketGroup[]
  ticketTypes: TicketType[]
  venues: Venue[]
  meta: MetaEntity[]
  _firstEventSession: EventSession | {}
}

/**
 * @deprecated Use fetchAvailableEvents().
 */
export function deprecatedFetchEvents(params: Dictionary = {}): Promise<EventAndFirstSession[] | EventAndSessions[]> {
  // Include member only events, but exclude link-only events.
  // @see https://github.com/stqry/tix-api/blob/master/service/events_available.go#L128
  // Mutation!
  params['hidden_type._in'] = 'public_browsable,public_member_only'
  return getEvents(params).then(nestEventsApiGetEventsResponse)
}

export function quantitiesPayload(quantities: TicketTypeQuantities) {
  return {
    ticket_types_required: Object.entries(quantities).map(([typeId, value]) => ({
      ticket_type_id: typeId,
      quantity: value.quantity,
    })),
  }
}

/**
 * @deprecated TODO Use fetchAvailableEvents().
 */
function getEvents(categoryOrOptions): Promise<GetEventsResponse> {
  categoryOrOptions = categoryOrOptions || {}

  const result: GetEventsResponse = {
    eventTemplates: [],
    eventSessions: [],
    ticketGroups: [],
    ticketTypes: [],
    venues: [],
    meta: [],
    _firstEventSession: {},
  }

  const embed = [
    'meta',
    'venue',
    // TODO Remove ticket_group and ticket_type.
    'ticket_group',
    'ticket_type',
  ]

  // If _ondate is given, then get all event sessions, otherwise only the
  // first session
  embed.push(categoryOrOptions._ondate ? 'event_session' : 'first_session')

  let query = {
    'ticket_group.hidden_type._in': 'public_browsable,public_member_only',
    _withmemberevents: true,
  }

  if (typeof categoryOrOptions === 'string') {
    query['event_template.category._in'] = categoryOrOptions
  } else {
    query = { ...query, ...categoryOrOptions }
  }

  return API.getCached('events/available', { query, embed })
    .then((response) => {
      result.eventTemplates = response.event_template!._data
      result.venues = response.venue!._data
      result.meta = response.meta!._data
      result.ticketGroups = response.ticket_group!._data
      result.ticketTypes = response.ticket_type!._data

      return response
    })
    .then((response) => {
      // Save all event sessions
      result.eventSessions = response.event_session!._data

      // Sort them, making it easier to later find the first event session
      sortEventSessionsByDate(result.eventSessions)

      const venuesByEventTemplate = {}

      // For each event template, find its first event session
      result.eventTemplates.forEach((eventTemplate) => {
        if (!venuesByEventTemplate[eventTemplate.id]) {
          venuesByEventTemplate[eventTemplate.id] = Venue.getVenueForEventTemplate(result.venues, eventTemplate)
        }

        result._firstEventSession[eventTemplate.id] = result.eventSessions.find(
          (eventSession) => eventSession.event_template_id === eventTemplate.id,
        )
      })

      // Add moment dates
      result.eventSessions = result.eventSessions.map((session) => {
        return normalizeSession(session as Session, venuesByEventTemplate[session.event_template_id])
      })

      return result
    })
}

/**
 * TODO Move to Events.ts.
 *
 * Document values; available, sold_out, ...?
 */
export function fetchAvailabilityCalendarForMinimumQuantities(
  eventTemplateId,
  quantities: TicketTypeQuantities,
): Promise<Dictionary> {
  return API.post(`events/${eventTemplateId}/calendar`, { body: quantitiesPayload(quantities) }).then(
    (response) => response.calendar as Dictionary,
  )
}

/**
 * TODO Move to Events.ts.
 *
 * Document values; available, sold_out, ...?
 */
export function fetchAvailabilityCalendar(
  eventId: string,
  ticketGroupIds: string[],
): Promise<Dict<DateAvailabilityStatus>> {
  const query = {
    'ticket_group_id._in': ticketGroupIds.join(','),
  }

  return API.getCached(`events/${eventId}/calendar`, { query }).then((response) => {
    return response.calendar as Dict<DateAvailabilityStatus>
  })
}
